// Read-only, context-free facsimiles of the leaves StudentPanel renders for a student
// (CPP / SQL Schema / Blockly Shared, plus Auto Grouping and Helper/Helpee).
//
// Each one reproduces the real component's student-branch DOM and reuses its real class
// names and stylesheet imports, so it looks the same without mounting the real component
// (which needs SessionContext, AuthContext, network calls, Pyodide, BlockMirror, ...).
// Nothing here reads context, fetches, writes storage, starts timers or adds window
// listeners. Source of each replica is noted above it; keep them in sync if those change.

import React, { useMemo } from 'react'
import { studentTestView, testsInStudentOrder } from '../testVisibility'
import { StudentTestCell } from '../TestCaseIO'
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import HistoryIcon from '@mui/icons-material/History'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HandshakeIcon from '@mui/icons-material/Handshake'
import OutputIcon from '@mui/icons-material/Output'
import NavigationIcon from '@mui/icons-material/Navigation'
import InfoIcon from '@mui/icons-material/Info'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'

// Same stylesheets the real components import.
import '../../../css/codeIssuePanel.scss' // StudentPanel
import '../../../css/taskcard.scss' // TaskCard
import '../../../css/UnitTestList.scss' // TestList
import '../../../css/code.scss' // CodeStu, CodeBar
import '../../../css/codearea.scss' // CodeArea
import '../../../css/chat.scss' // Chat
import '../../../css/audio/audioGroupInput.scss' // GroupInputComponent
import '../../../css/studentview-panel.scss'

const CODEBAR_TOOLTIP = 'Students run their code here — read-only in this view'

// --- HeaderBanner (src/component/topBar/HeaderBanner.jsx), signed-up student (role 4) ---
// Rendered by Home.jsx above StudentPanel. Desktop (md+) layout only.
// showLinks=false matches HeaderBanner for a student of a Python session (with or without AI
// chat), where the Session List / Homepage links are hidden (HeaderBanner hidesStudentNav).
export function AppBarFacsimile({ showLinks = true }) {
  return (
    <AppBar position="static" className="svp-appbar">
      <Container maxWidth="m" sx={{ height: 1 }}>
        <Toolbar disableGutters className="svp-appbar-toolbar">
          <Typography variant="h6" noWrap component="span" className="svp-appbar-brand">
            VizPI
          </Typography>
          <Box className="svp-appbar-links">
            {showLinks && (
              <>
                <Button className="svp-appbar-link" tabIndex={-1}>
                  Session List
                </Button>
                <Button className="svp-appbar-link" tabIndex={-1}>
                  Homepage
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ flexGrow: 0 }}>
            <Avatar className="svp-appbar-avatar" />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

// --- PeerInstruction (src/component/student-components/PeerInstruction.jsx) ---
// Stepper over the columns; activeStep is 0 until the session is grouped.
export function PeerInstructionFacsimile({ activeStep = 0 }) {
  const steps = ['Individual Attempt', 'Group Attempt']
  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  )
}

// --- TaskCard (src/component/topBar/TaskCard.jsx), student branch (role 3/4) ---
// Students see only the disabled task textarea and an empty action bar. The real card
// does not show the session subject anywhere; `subject` only labels the textarea for
// assistive tech.
// rootProps (optional): extra attributes for the .taskCard root, e.g. the live preview's
// region markers. Omitted, the markup is unchanged.
export function TaskCardFacsimile({ subject = '', task = '', rootProps = null }) {
  return (
    <div className="taskCard" {...rootProps}>
      <Typography variant="h6" fontWeight={'light'} gutterBottom>
        Task Description
      </Typography>
      <div className="task-area">
        <textarea
          className="exercise-area"
          disabled
          readOnly
          value={task || ''}
          aria-label={subject ? `Task description: ${subject}` : 'Task description'}
        />
      </div>
      <div className="exerciseBar" />
    </div>
  )
}

// --- TestList (src/component/topBar/TestList.jsx), student table (role 3/4) ---
// Before a student has run anything every result is null, which the real PassIcon draws
// as a grey BlockIcon.
// TestList's PassIcon: null/undefined grey block, true green check, false red cross.
function TestResultIcon({ result }) {
  if (result == null) return <BlockIcon sx={{ color: '#808080' }} />
  return result ? (
    <DoneIcon sx={{ color: '#4caf50' }} />
  ) : (
    <CloseIcon sx={{ color: '#f44336' }} />
  )
}

// results (optional): per-test outcomes from a run, drawn with TestResultIcon. Omitted,
// every row is grey as before. rootProps (optional): extra attributes for the .scafold root.
export function TestTableFacsimile({
  testList = [],
  placeholderCount = 3,
  results = null,
  rootProps = null,
}) {
  const rows = Array.isArray(testList) ? testList : []
  const placeholders =
    rows.length === 0
      ? Array.from({ length: Math.max(0, Math.floor(Number(placeholderCount) || 0)) }, (_, i) => i)
      : []
  return (
    <div className="scafold" {...rootProps}>
      <Typography variant="h6" fontWeight={'light'} gutterBottom>
        Test Cases
      </Typography>
      <div className="add-div">
        <TableContainer>
          <Table className="testTable">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography fontSize={'+2'} fontWeight={'bold'}>
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontSize={'+2'} fontWeight={'bold'}>
                    Result
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testsInStudentOrder(rows).map(({ index }) => (
                <TableRow key={index}>
                  <TableCell>
                    {/* As TestList's student table: public tests first, with their input
                        and expected output. */}
                    <StudentTestCell
                      view={studentTestView(rows, index)}
                      renderName={(name) => <Typography fontSize={'+1'}>{name}</Typography>}
                    />
                  </TableCell>
                  <TableCell>
                    {Array.isArray(results) ? (
                      <TestResultIcon result={results[index]} />
                    ) : (
                      <BlockIcon sx={{ color: '#808080' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {placeholders.map((i) => (
                <TableRow key={`svp-placeholder-${i}`} className="svp-test-placeholder">
                  <TableCell>
                    <Typography fontSize={'+1'}>
                      <span className="svp-test-placeholder-label">
                        <span className="svp-test-placeholder-name">Test {i + 1}</span>
                        <span className="svp-test-placeholder-note">
                          (added in Testcases step)
                        </span>
                      </span>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <BlockIcon
                      className="svp-test-placeholder-icon"
                      sx={{ color: '#808080' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}

// --- CodeStu wrapper (src/component/code/CodeStu.jsx) ---
// code.scss scopes every student code style under .code-container .code-stud .container,
// so the code-column leaves below only look right inside this frame. `after` fills the
// second .container (SQL's "Your Output" diff lives there).
export function CodeStuFrame({ children = null, after = null }) {
  return (
    <div className="code-container">
      <div className="code-stud">
        <div className="container">{children}</div>
        <div className="container">{after}</div>
      </div>
    </div>
  )
}

// --- CodeBar (src/component/code/CodeBar.jsx), student buttons ---
// A fresh student has latestCodeId '' and a template selectedCode with no id, so the real
// bar also shows "Latest code" until their first run (showLatestCode). SQL and
// Helper/Helpee add the "Ask for help" button (helpLabel).
export function CodeBarFacsimile({ runLabel = 'Run', showLatestCode = true, helpLabel = null }) {
  return (
    <div className="codeBar">
      <div className="codeBar">
        <div />
        <div
          style={{
            marginLeft: '5px',
            marginRight: '5px',
            display: 'flex',
            justifyContent: 'space-evenly',
            alignContent: 'center',
          }}>
          <Tooltip title={CODEBAR_TOOLTIP}>
            <span className="svp-codebar-tip">
              <button type="button" className="codeButton" disabled>
                <PlayArrowRoundedIcon sx={{ marginRight: 1, color: '#00921A' }} /> {runLabel}
              </button>
              <button type="button" className="codeButton" disabled>
                <HistoryIcon sx={{ marginRight: 1, color: '#EAAE00' }} /> Reset Code
              </button>
              {showLatestCode && (
                <button type="button" className="codeButton" disabled>
                  <RestartAltIcon sx={{ marginRight: 1, color: '#2473DE' }} /> Latest code
                </button>
              )}
            </span>
          </Tooltip>
        </div>
      </div>
      {helpLabel && (
        <div>
          <Tooltip title={CODEBAR_TOOLTIP}>
            <span className="svp-codebar-tip">
              <button type="button" className="codeButton" disabled>
                <HandshakeIcon sx={{ marginRight: 1, color: '#55D246' }} /> {helpLabel}
              </button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

// --- CodeArea (src/component/code/CodeArea.jsx), text-editor branch ---
// The real student editor uses python() highlighting and the default light theme for
// every StudentPanel type (CPP and SQL included), so the facsimile does too; `language`
// is recorded on the wrapper only.
export function CodeEditorFacsimile({ code = '', language = 'cpp' }) {
  const extensions = useMemo(() => [python(), indentUnit.of('    ')], [])
  return (
    <div className="full-size" style={{ height: '250px' }} data-language={language}>
      <div className="code-mirror-wrapper">
        <span>
          <CodeMirror
            className="codeTextArea"
            value={code || ''}
            extensions={extensions}
            editable={false}
            readOnly
          />
        </span>
      </div>
    </div>
  )
}

// --- CodeArea (src/component/code/CodeArea.jsx), Blockly branch ---
// Never mounts BlockpyMirror (it binds to a fixed DOM id and window.Blockly). Static copy
// of BlockMirror's split view with toolbox 'ct2' as CodeArea configures it: category
// names, colours and sizes are taken from the real rendered editor. The text pane shows
// `code` (the template the student starts from) with CodeMirror 5's Python colours; the
// blocks BlockMirror would generate from it are not drawn. Box sizes mirror the real
// ones: a 500px .full-size holding the 500px editor, then the (empty, 250px)
// .code-mirror-wrapper the real component still renders under it.
const BLOCKLY_TOOLBOX = [
  { name: 'Memory', colour: '#5b6da5' },
  { sep: true },
  { name: 'Expressions', group: true },
  { name: 'Constants', colour: '#5ba55b', child: true },
  { name: 'Variables', colour: '#5b6da5', child: true },
  { name: 'Calculation', colour: '#5b99a5', child: true },
  { name: 'Conversion', colour: '#5ba55b', child: true },
  { name: 'Conditions', colour: '#a55b6d', child: true },
  { name: 'Input', colour: '#5ba55b', child: true },
  { sep: true },
  { name: 'Operations', group: true },
  { name: 'Assignment', colour: '#5b6da5', child: true },
  { name: 'Output', colour: '#5ba574', child: true },
  { sep: true },
  { name: 'Control', group: true },
  { name: 'Decision', colour: '#805ba5', child: true },
  { name: 'Iteration', colour: '#805ba5', child: true },
]

const PY_KEYWORDS = new Set([
  'and', 'as', 'assert', 'break', 'class', 'continue', 'def', 'del', 'elif', 'else',
  'except', 'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda',
  'nonlocal', 'not', 'or', 'pass', 'raise', 'return', 'try', 'while', 'with', 'yield',
  'True', 'False', 'None',
])

// Minimal CodeMirror-5-style colouring: keywords, and the name after def/class.
function highlightPythonLine(line) {
  const parts = line.split(/(\b[A-Za-z_][A-Za-z0-9_]*\b)/)
  let afterDef = false
  return parts.map((part, i) => {
    if (PY_KEYWORDS.has(part)) {
      afterDef = part === 'def' || part === 'class'
      return (
        <span key={i} className="svp-cm-keyword">
          {part}
        </span>
      )
    }
    if (afterDef && /^[A-Za-z_]/.test(part)) {
      afterDef = false
      return (
        <span key={i} className="svp-cm-def">
          {part}
        </span>
      )
    }
    return part
  })
}

export function BlocklyFacsimile({ code = '' }) {
  const lines = String(code || '').split('\n')
  return (
    <div className="full-size" style={{ height: '500px' }}>
      <span>
        <div className="svp-blockly" aria-label="Blockly workspace (preview)">
          <div className="svp-blockly-blocks">
            <div className="svp-blockly-toolbox">
              {BLOCKLY_TOOLBOX.map((row, i) =>
                row.sep ? (
                  <div key={`sep-${i}`} className="svp-blockly-sep" />
                ) : (
                  <div
                    key={row.name}
                    className={`svp-blockly-row${row.child ? ' svp-blockly-row--child' : ''}`}
                    style={row.colour ? { borderLeftColor: row.colour } : undefined}>
                    <span className="svp-blockly-icon">{row.group ? '\u25BC' : ''}</span>
                    <span className="svp-blockly-label">{row.name}</span>
                  </div>
                )
              )}
            </div>
            <div className="svp-blockly-workspace">
              <div className="svp-blockly-scrollbar" />
            </div>
          </div>
          <div className="svp-blockly-text">
            <div className="svp-blockly-gutter">
              {lines.map((_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>
            <div className="svp-blockly-lines">
              {lines.map((line, i) => (
                <div key={i}>{line ? highlightPythonLine(line) : '\u200b'}</div>
              ))}
            </div>
          </div>
        </div>
      </span>
      <div className="code-mirror-wrapper" />
    </div>
  )
}

// --- CodeStu output panel (src/component/code/CodeStu.jsx) + PythonInterpreter's <pre> ---
// PythonInterpreter itself reads MessageContext/AuthContext, so its markup is replicated.
// rootProps (optional): extra attributes for the .codeOutputWrapper root.
export function OutputFacsimile({ output = '', rootProps = null }) {
  return (
    <div className="codeOutputWrapper" {...rootProps}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'light',
          alignItems: 'center',
          display: 'flex',
          padding: '3px',
        }}
        gutterBottom>
        <div
          style={{
            backgroundColor: '#f2f2f2',
            padding: '7px',
            paddingRight: '13px',
            borderRadius: '10px',
            marginRight: '10px',
          }}>
          <OutputIcon sx={{ marginRight: '8px', color: '#4699D2' }} />
          Output
        </div>
      </Typography>
      <InterpreterPre output={output} />
    </div>
  )
}

function InterpreterPre({ output = '' }) {
  return (
    <pre
      className="codeOutputArea"
      style={{
        whiteSpace: 'pre-wrap',
        fontFamily: 'monospace',
        margin: 0,
        padding: '8px',
        overflow: 'auto',
      }}>
      {output}
    </pre>
  )
}

// --- StudentPanel's SQL schema browser (left column) ---
// Until getSchemaBySessionId resolves the table is empty; `schema` ({ columns, values },
// the shape StudentPanel's currentTable uses) can pre-fill it. `output` is what the real
// box's PythonInterpreter shows (CodeStu.runSQL's message / Success / Failed); rootProps
// (optional) are extra attributes for the box's root.
export function SqlSchemaFacsimile({ schema = null, output = '', rootProps = null }) {
  const columns = Array.isArray(schema?.columns) ? schema.columns : []
  const values = Array.isArray(schema?.values) ? schema.values : []
  return (
    <div
      className="taskCard"
      style={{
        border: '1px solid #e1e1e1',
        height: '100%',
        width: '100%',
        display: 'flex',
        overflowX: 'visible',
        overflowY: 'auto',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
      {...rootProps}>
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Link variant="hover" sx={{ padding: '10px', cursor: 'pointer' }}>
          Companydb
        </Link>
      </Breadcrumbs>
      <table>
        <thead>
          <tr>
            {columns.map((columnName, i) => (
              <td key={i}>{columnName}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {values.map((row, i) => (
            <tr key={i}>
              {(Array.isArray(row) ? row : []).map((value, j) => (
                <td key={j}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="codeOutputWrapper">
        <Typography variant="h6" sx={{ fontWeight: 'light' }}>
          Output
        </Typography>
        <InterpreterPre output={output} />
      </div>
    </div>
  )
}

// --- CodeStu's SQL result diff (second .container), before any run ---
export function SqlResultFacsimile() {
  return (
    <span>
      <div style={{ display: 'flex', overflowX: 'scroll', marginTop: '.5em' }}>
        <div>
          <Typography>Your Output</Typography>
          <div>
            <div>
              <Grid container />
            </div>
          </div>
        </div>
        <Divider orientation="vertical" flexItem />
        <div>
          <div>
            <div>
              <Grid container />
            </div>
          </div>
        </div>
      </div>
    </span>
  )
}

// --- Chat (src/component/chat/Chat.jsx), student view with an empty history ---
// The real group tabs come from MUI <Tabs>, which registers a window resize listener, so
// the strip is drawn statically around a real MUI <Tab>. `suggestions` are the quick-send
// buttons; the real Chat shows 'How did you complete the task?' once the student's
// template code has loaded.
export function PeerChatFacsimile({
  label = 'Group chat',
  suggestions = ['How did you complete the task?'],
  caption = 'Peer chat — students talk to their group here',
}) {
  return (
    <div className="chat">
      <div className="svp-chat-tabs">
        {label && (
          <Tab
            label={label}
            selected
            fullWidth
            textColor="primary"
            disableRipple
            tabIndex={-1}
            sx={{
              bgcolor: '#C0C0C0',
              borderRadius: 1,
              border: 1,
              borderColor: 'primary.main',
            }}
          />
        )}
        <span className="svp-chat-tabs-indicator" />
      </div>
      {/* The caption sits where the empty history would be, so it does not shift the
          layout (the real history shows Messages' placeholder text there). */}
      <div className="chat-history">
        {caption && <div className="svp-caption">{caption}</div>}
        <div className="messages" />
      </div>
      <div className="svp-chat-suggestions">
        {(suggestions || []).map((question) => (
          <Button
            key={question}
            color="primary"
            variant="contained"
            size="small"
            tabIndex={-1}
            disableRipple
            sx={{ alignSelf: 'flex-end', mb: '2px', borderRadius: 28 }}
            endIcon={<NavigationIcon />}>
            {question}
          </Button>
        ))}
      </div>
      <form className="message-form" onSubmit={(e) => e.preventDefault()}>
        <textarea
          className="message-input"
          value=""
          disabled
          readOnly
          placeholder="Use Shift + Enter for new line"
        />
        <button type="button" className="send-button" disabled>
          Send to
        </button>
        <Select value="group" readOnly tabIndex={-1}>
          <MenuItem value="instructor">Instructor &amp; TA</MenuItem>
          <MenuItem value="group">Group</MenuItem>
        </Select>
      </form>
    </div>
  )
}

// --- Chat's early-return notices (no group yet), e.g. Helper/Helpee and SQL ---
export function ChatNoticeFacsimile({ children = null }) {
  return <div className="chat">{children}</div>
}

// --- GroupInputComponent (src/component/audioSTU/AudioGroupInput.jsx) ---
// What a Blockly student sees in the right column until they enter a (Zoom) room number;
// after that it becomes AudioChat. The real guidance <img> points at a placeholder path
// that 404s, so its alt text is drawn instead of issuing that request.
export function GroupRoomInputFacsimile({
  caption = 'Group room — Blockly students join their group by room number here',
}) {
  return (
    <div className="room-input-container">
      <div className="guidance-image">
        <span className="svp-broken-img">
          <span className="svp-broken-img-icon" aria-hidden="true" />
          Guidance
        </span>
      </div>
      <div className="input-wrapper">
        <input
          type="number"
          className="room-input"
          placeholder="Enter Room Number"
          disabled
          readOnly
        />
        <Tooltip title="Please enter your Zoom group number." arrow>
          <IconButton className="info-icon" tabIndex={-1}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </div>
      <button type="button" className="submit-button" disabled>
        Enter
      </button>
      {caption && (
        <div className="svp-caption svp-caption--zero">
          <span>{caption}</span>
        </div>
      )}
    </div>
  )
}
